import { commonText } from "./commonPresentation";

const messages = {
  "common.invalidCsrf": "feedbackInvalidCsrf",
  "common.unauthorized": "feedbackUnauthorized",
  "common.maintenance": "feedbackMaintenance",
  "common.serverError": "feedbackServerError",
  "common.badRequest": "feedbackBadRequest",
} as const;
export type SharedRemoteFeedback = {
  remote: true;
  fallback: string;
  descriptor: { code: keyof typeof messages; params: Record<string, never> };
};
const own = (value: object, key: PropertyKey) => Object.prototype.hasOwnProperty.call(value, key);
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value)
  && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

export function sharedFeedbackValue(fallback: unknown, descriptor: unknown): SharedRemoteFeedback | undefined {
  if (typeof fallback !== "string" || !record(descriptor) || !own(descriptor, "code") || typeof descriptor.code !== "string"
    || !own(messages, descriptor.code) || !own(descriptor, "params") || !record(descriptor.params)
    || Object.keys(descriptor.params).length || Object.keys(descriptor).some(key => key !== "code" && key !== "params")) return undefined;
  return { remote: true, fallback, descriptor: { code: descriptor.code as keyof typeof messages, params: {} } };
}
/** Only explicit source metadata is recognized; identical raw text remains literal. */
export function sharedRemoteFeedback(response: unknown): SharedRemoteFeedback | undefined {
  if (!record(response)) return undefined;
  const nested = record(response.error) ? response.error : response;
  const fallback = typeof nested.message === "string" ? nested.message : response.message;
  return sharedFeedbackValue(fallback, nested.messagePresentation ?? nested.presentation ?? response.messagePresentation ?? response.presentation);
}
export function isSharedRemoteFeedback(value: unknown): value is SharedRemoteFeedback {
  return record(value) && value.remote === true && !!sharedFeedbackValue(value.fallback, value.descriptor);
}
export function sharedFeedbackText(value: SharedRemoteFeedback): string {
  const fallback = value.fallback;
  return isSharedRemoteFeedback(value) ? commonText(messages[value.descriptor.code]) : fallback;
}
